import { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';

import type { SortingProps } from '@/_containers';
import { CellAvatar, CellFallback, CellName, ColumnHeader, selectBoxColDef } from '@/_containers';
import { InvoiceEmailActionsCell } from '@/_pages/invoices/shared/components/invoice-email-actions-cell';
import { InvoicePdfActionsCell } from '@/_pages/invoices/shared/components/invoice-pdf-actions-cell';
import { InvoiceStatusCell } from '@/_pages/invoices/shared/components/invoice-status-cell';
import { currencyFormat, dateTime, nameResolver } from '@/_shared';

import type { InvoiceEmailPageCallbacks } from '../shared/invoice-email-page-callbacks';
import type { InvoiceQuerySchema } from '../shared/schemas';

export function useDefineColumns(sorting: SortingProps, emailCallbacks?: InvoiceEmailPageCallbacks) {
  return useMemo(() => {
    const columns: ColumnDef<InvoiceQuerySchema>[] = [
      ...selectBoxColDef<InvoiceQuerySchema>(),
      {
        id: 'invoice_number',
        meta: { name: 'Invoice #', className: 'max-w-28 w-28' },
        accessorFn: (row) => ({ invoice_number: row?.invoice_number ?? '—', id: row.id }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} />,
        cell: ({ row }) => {
          const { invoice_number, id } = row.getValue('invoice_number') as { invoice_number: string; id: number };
          return (
            <CellName
              name={invoice_number}
              className="text-wrap whitespace-break-spaces"
              showAvatar={false}
              to="/invoices/$id"
              params={{ id: `${id}` }}
            />
          );
        },
        enableHiding: false,
      },
      {
        id: 'invoice_date',
        meta: { name: 'Invoice Date', className: 'max-w-34 w-34' },
        accessorFn: (row) => ({ invoice_date: row?.invoice_date ?? '—' }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} />,
        cell: ({ row }) => {
          const { invoice_date } = row.getValue('invoice_date') as {
            invoice_date: string;
          };
          return <CellFallback text={invoice_date ? dateTime.format(invoice_date) : '—'} />;
        },
      },
      {
        id: 'account_number',
        meta: { name: 'Account #', className: 'max-w-30 w-30' },
        accessorFn: (row) => row?.client?.account_number ?? null,
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} />,
        cell: ({ row }) => {
          const accountNumber = row.getValue('account_number') as number | null;
          return <CellFallback text={accountNumber != null ? String(accountNumber) : '—'} />;
        },
      },
      {
        id: 'client',
        meta: { name: 'Company' },
        accessorFn: (row) => ({
          name: nameResolver(row?.client?.name, undefined, 'Company'),
          src: row.client?.logo?.at(0)?.url,
          id: row.client?.id,
        }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} />,
        cell: ({ row }) => {
          const { name, src, id } = row.getValue('client') as { name: string; src?: string; id?: number };
          if (!id) {
            return <CellFallback text="—" />;
          }
          return <CellName name={name} src={src ?? ''} className="text-wrap" avatarShape="rectangle" />;
        },
      },
      {
        id: 'invoice_amount',
        meta: { name: 'Invoice Total', className: 'text-right w-36 max-w-36' },
        accessorFn: (row) => ({ invoice_amount: row?.invoice_amount ?? '—' }),
        header: ({ column }) => <ColumnHeader className="text-wrap" sorting={sorting} column={column} align="right" />,
        cell: ({ row }) => {
          const { invoice_amount } = row.getValue('invoice_amount') as {
            invoice_amount: number | string;
          };
          return <CellFallback align="right" text={currencyFormat(invoice_amount ?? 0)} />;
        },
      },
      {
        id: 'due_date',
        meta: { name: 'Due Date', className: 'w-28 max-w-28' },
        accessorFn: (row) => ({ due_date: row?.due_date ?? '—' }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} />,
        cell: ({ row }) => {
          const { due_date } = row.getValue('due_date') as {
            due_date: string;
          };
          return <CellFallback text={due_date ? dateTime.format(due_date) : '—'} />;
        },
      },
      {
        id: 'brand',
        meta: { name: 'Brand', className: 'w-24 max-w-24' },
        accessorFn: (row) => ({
          name: row?.brand?.name ?? '—',
          src: row.brand?.icon?.at(0)?.url,
          id: row.brand?.id,
        }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} align="center" />,
        cell: ({ row }) => {
          const { name, src, id } = row.getValue('brand') as { name: string; src?: string; id?: number };
          if (!id) {
            return <CellFallback align="center" text="—" />;
          }
          return (
            <CellAvatar
              src={src}
              name={name}
              to="/settings/brands/$id"
              params={{ id: `${id}` }}
              state={{ naturalBack: true }}
              forceShow
            />
          );
        },
      },
      {
        accessorKey: 'status',
        meta: { name: 'Status', className: 'text-center w-24 max-w-24' },
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} align="center" />,
        cell: ({ row }) => {
          const status = row.getValue('status');
          return <InvoiceStatusCell status={status as InvoiceQuerySchema['status']} />;
        },
      },
      {
        id: 'invoice_pdfs',
        meta: { name: 'PDF', className: 'w-20' },
        accessorFn: (row) => ({
          printable: row.printable_invoices_pdf?.at(0)?.url ?? null,
          digital: row.digital_invoices_pdf?.at(0)?.url ?? null,
        }),
        header: ({ column }) => <ColumnHeader sorting={sorting} column={column} align="center" />,
        cell: ({ row }) => {
          const { printable, digital } = row.getValue('invoice_pdfs') as {
            printable: string | null;
            digital: string | null;
          };
          return <InvoicePdfActionsCell printableUrl={printable} digitalUrl={digital} />;
        },
        enableSorting: false,
      },
      {
        id: 'invoice_email',
        meta: { name: 'Email', className: 'w-14 max-w-14' },
        header: ({ column }) => <ColumnHeader column={column} align="center" />,
        cell: ({ row }) => (
          <InvoiceEmailActionsCell
            invoice={row.original}
            onOpenSendModal={() => emailCallbacks?.openEmailModal(row.original)}
          />
        ),
        enableSorting: false,
      },
    ];
    return columns;
  }, [sorting, emailCallbacks]);
}
